import { MAX_SKILLS_PER_CHAT } from '@server/types';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import TextExtension from '@tiptap/extension-text';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { ArrowUp, LoaderCircle, Square, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { cn } from '@/_shared/lib/utils';

import { docToText, isDocEmpty, PANEL_MENTION_NODE, readMentionIds, textToDoc } from '../lib/composer-document';
import { MENTION_TRIGGER, type PanelMention } from '../lib/panel-mentions';
import { PanelMentionNode } from './composer/panel-mention-node';
import { createSuggestionExtension, type SuggestionState } from './composer/suggestion-extension';
import { PanelMentionMenu } from './PanelMentionMenu';
import { SkillMenu } from './SkillMenu';

interface PendingSkill {
  id: string;
  title: string;
}

/**
 * EU AI Act Article 50(1) — the user must be told they are interacting with an AI system.
 * "Lookout" does not say AI on its own, so this copy has to.
 */
const AI_DISCLOSURE_TEXT = 'Lookout is an AI assistant and can make mistakes. Check important info.';

interface MessageInputProps {
  onSend: (content: string, invokedSkillIds?: string[], referencedPanelIds?: string[]) => void;
  /**
   * A turn is in progress (request pending and/or agent streaming), or the thread cannot take
   * another message. Blocks Send and Enter. The button shows a spinner, or Stop once the turn can
   * be interrupted.
   */
  disabled?: boolean;
  /**
   * The field itself cannot be edited. Defaults to `disabled`, which is what a closed thread and a
   * session that is not ready still want.
   *
   * A running turn passes `false`: the next message can be drafted, and Send and Enter still do
   * nothing until `disabled` clears.
   */
  locked?: boolean;
  /** The agent turn has loaded and can be stopped. Button becomes Stop when `disabled` and `onStop` are also set. */
  agentLoaded?: boolean;
  /** A Stop command was accepted; the turn has not yet reached terminal database status. */
  stopping?: boolean;
  /** Stop has stayed `stop_requested` long enough that the owner may be gone. */
  stopStalled?: boolean;
  stopStatusError?: boolean;
  onRetryStopStatus?: () => void;
  variant?: 'hero' | 'chat' | 'sidebar';
  /** Overrides the variant's default placeholder (e.g. the routine builder isn't "this dashboard"). */
  placeholder?: string;
  /** Skills already loaded into this chat session (non-removable, shown dimmed). */
  loadedSkills?: { id: string; title: string }[];
  /** Needed by SkillMenu to filter against the workspace. When absent, slash menu is suppressed. */
  workspaceSlug?: string;
  /** Gate: only show slash menu when workspace has at least one active skill. */
  hasInvokableSkills?: boolean;
  /** When provided AND `disabled` and `agentLoaded` are both true, replaces the spinner with a stop button. */
  onStop?: () => void;
  /**
   * Optional content to prefill into the composer, applied when it is empty — or regardless, when
   * `prefillReplace` is set.
   */
  prefillValue?: string;
  /**
   * The panels `prefillValue` named, as the turn sent them.
   *
   * A restored prompt arrives as plain text, so its mentions have to become nodes again. Matching the
   * labels against `panelOptions` would bind each mention to whatever carries that label now, and a
   * rename or a reorder since the stop changes that. These carry the original ids. Empty or absent
   * falls back to `panelOptions`, which is all an abort recorded before this existed can offer.
   */
  prefillPanels?: PanelMention[];
  /** Bumped by the parent each time a new prefill should be applied (allows the same value to retrigger). */
  prefillNonce?: number;
  /**
   * When true, apply `prefillValue` even if the composer already has a draft (example chips on New
   * chat). The draft is replaced, not merged. Cmd+Z restores it. Abort-restore leaves this false.
   */
  prefillReplace?: boolean;
  /** Bumped when an interactive block resolves; focuses the composer once the field can be edited (a11y). 0 holds it. */
  autoFocusNonce?: number;
  /** When true, the parent owns outer padding (e.g. chat composer dock). */
  embedded?: boolean;
  /** Panels "@" can name. Absent or empty removes the mention trigger and its hint. */
  panelOptions?: PanelMention[];
  /** Fired when the menu writes a mention, so the caller can record which entry point was used. */
  onPanelMentioned?: (mention: PanelMention) => void;
  /**
   * A mention to write in from outside — the ✨ on a panel is the other entry point.
   *
   * Queued rather than pushed: the composer mounts with the sidebar, and the editor is built in an
   * effect after that. An imperative call from the page arrived before either existed and was
   * dropped, so the first ✨ of a session did nothing until the sidebar had been opened once.
   */
  pendingMention?: PanelMention;
  /** Bumped for each mention to write, so the same panel can be sent twice. */
  pendingMentionNonce?: number;
}

/**
 * The sentence the composer sends when a skill chip is the whole message. It is written in the user's
 * voice because it becomes their turn in the transcript and, being the first message, the chat's title.
 */
function runSkillsSentence(skills: { title: string }[]): string {
  const names = skills.map((skill) => skill.title);
  if (names.length === 1) return `Run ${names[0]}.`;
  const last = names[names.length - 1];
  return `Run ${names.slice(0, -1).join(', ')} and ${last}.`;
}

/**
 * The chat composer.
 *
 * The draft is a document, not a string. A panel mention is an atom node holding the panel id, so
 * one Backspace removes the whole mention, a selection cannot take half of it, and the ids a message
 * carries are read from the document rather than matched out of the text.
 *
 * What travels on the wire is unchanged: the document serializes to the same plain text a textarea
 * produced, with each mention written as `@Label`.
 */
export function MessageInput({
  onSend,
  disabled = false,
  locked,
  agentLoaded = false,
  stopping = false,
  stopStalled = false,
  stopStatusError = false,
  onRetryStopStatus,
  variant = 'chat',
  placeholder,
  loadedSkills = [],
  workspaceSlug,
  hasInvokableSkills = false,
  onStop,
  prefillValue,
  prefillPanels,
  prefillNonce,
  prefillReplace = false,
  autoFocusNonce,
  embedded = false,
  panelOptions,
  onPanelMentioned,
  pendingMention,
  pendingMentionNonce,
}: MessageInputProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorId = useId();
  const skillMenuId = useId();
  const panelMenuId = useId();

  /**
   * The option a screen reader should announce.
   *
   * `aria-activedescendant` only has an effect on the focused element, and focus never leaves the
   * composer while a menu is open. So the menus report their active row, and the composer carries it.
   */
  const [activeOptionId, setActiveOptionId] = useState<string | undefined>(undefined);

  // The skill trigger keeps the start-of-input rule it has always used, and SkillMenu keeps fetching
  // and rendering itself. Only the panel trigger needs document ranges from a suggestion plugin.
  /** The draft the skill menu was last dismissed on. Any later edit opens it again. */
  const [slashDismissedFor, setSlashDismissedFor] = useState<string | null>(null);
  const [pendingSkills, setPendingSkills] = useState<PendingSkill[]>([]);

  const [mentionState, setMentionState] = useState<SuggestionState<PanelMention> | null>(null);

  const isHero = variant === 'hero';
  const isSidebar = variant === 'sidebar';
  const canMentionPanels = !!panelOptions?.length;
  // Send and the field are separate. A running turn blocks Send and leaves the field open; a closed
  // thread blocks both. Callers that omit `locked` keep the old "disabled means you cannot type".
  const editorLocked = locked ?? disabled;

  // ProseMirror plugins are built once, so everything they read travels through a ref.
  const panelOptionsRef = useRef(panelOptions);
  const prefillPanelsRef = useRef(prefillPanels);
  const mentionedIdsRef = useRef<Set<string>>(new Set());
  const onPanelMentionedRef = useRef(onPanelMentioned);
  const submitRef = useRef<() => void>(() => undefined);
  const menuOpenRef = useRef(false);

  // Every ref above is written after commit, never during render. Each one is read from a keystroke
  // or a ProseMirror transaction, and both of those happen long after the commit that set it.
  useEffect(() => {
    panelOptionsRef.current = panelOptions;
    prefillPanelsRef.current = prefillPanels;
    onPanelMentionedRef.current = onPanelMentioned;
  });

  const resolvedPlaceholder =
    placeholder ??
    (disabled
      ? agentLoaded
        ? 'Lookout is responding...'
        : 'Loading...'
      : isHero
        ? "Describe what you'd like to investigate..."
        : isSidebar
          ? 'Ask about this dashboard...'
          : 'Ask about your data pipeline...');
  const placeholderRef = useRef(resolvedPlaceholder);

  /**
   * The field's accessible name, which must not change while the user is in it.
   *
   * `resolvedPlaceholder` swaps to "Lookout is responding..." for the length of a turn. Binding the
   * name to it renamed the field mid-interaction, and status is not a name. A field that cannot be
   * edited announces that with `aria-readonly`. The status text still shows as the visual placeholder.
   */
  const accessibleName =
    placeholder ??
    (isHero
      ? "Describe what you'd like to investigate..."
      : isSidebar
        ? 'Ask about this dashboard...'
        : 'Ask about your data pipeline...');

  const panelSuggestion = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- `items` and `apply` read these refs from a ProseMirror transaction, never during render
      createSuggestionExtension<PanelMention>({
        name: 'panelMentionSuggestion',
        char: MENTION_TRIGGER,
        // Labels hold spaces, so the query must too. The candidate list going empty is what closes
        // the menu again, and that is what frees the Enter key to send the message.
        allowSpaces: true,
        items: (query) => {
          const available = (panelOptionsRef.current ?? []).filter((m) => !mentionedIdsRef.current.has(m.id));
          const needle = query.trim().toLowerCase();
          return needle ? available.filter((m) => m.label.toLowerCase().includes(needle)) : available;
        },
        onStateChange: setMentionState,
        apply: ({ editor: current, range, item }) => {
          current
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: PANEL_MENTION_NODE, attrs: { id: item.id, label: item.label } },
              { type: 'text', text: ' ' },
            ])
            .run();
          onPanelMentionedRef.current?.(item);
        },
      }),
    [],
  );

  /*
   * Paste is left to the schema.
   *
   * There is deliberately no `transformPastedHTML` here. It runs before parsing, so stripping tags
   * with a regex also strips ProseMirror's own slice wrapper and the `span[data-panel-id]` this
   * schema parses a mention from. That flattened block structure — two pasted paragraphs arrived as
   * one run-on line — and made copying a mention paste it back as dead text.
   *
   * The schema registers no marks and only one block type, so bold, italics and headings cannot
   * survive a paste anyway. The parser drops them, and keeps the paragraphs and the mentions.
   */
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      TextExtension,
      HardBreak,
      UndoRedo,
      // eslint-disable-next-line react-hooks/refs -- the placeholder callback reads its ref while the editor paints decorations, on a transaction
      Placeholder.configure({
        placeholder: () => placeholderRef.current,
        // A closed thread locks the field, and that is when the placeholder says why ("This thread
        // is closed", "Reconnecting…"). The default hides the placeholder whenever the editor is
        // not editable, which left an empty box for the whole lock.
        showOnlyWhenEditable: false,
      }),
      PanelMentionNode,
      panelSuggestion,
    ],
    editable: !editorLocked,
    editorProps: {
      attributes: {
        id: editorId,
        role: 'textbox',
        'aria-multiline': 'true',
        // A content-editable element has no placeholder attribute, so the prompt text is the
        // accessible name. That is what a placeholder does for a screen reader in any case.
        'aria-label': accessibleName,
        class: cn(
          'composer-editor relative max-h-[12.5rem] w-full overflow-y-auto bg-transparent text-foreground outline-none',
          isHero
            ? 'min-h-28 px-6 pt-5 pb-2 text-[1.0625rem] leading-7'
            : isSidebar
              ? 'px-3 pt-2 pb-1 text-sm'
              : 'px-4 pt-3 pb-1 text-sm',
        ),
      },
      // Direct editor props run before plugin props, so an open menu is given first refusal on these
      // keys. Without the guard, Enter would send while the user was still choosing a panel.
      handleKeyDown: (_view, event) => {
        if (menuOpenRef.current) return false;
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          submitRef.current();
          return true;
        }
        return false;
      },
    },
  });

  /**
   * The document serialized to plain text — what a send carries, and what the skill trigger reads.
   *
   * Read through `useEditorState` rather than an `onUpdate` callback: the hook subscribes to the
   * editor's transactions, so React re-renders with the document rather than with a copy of it that
   * a missed callback could leave behind.
   */
  const { text, empty, mentionedIds } = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      const doc = current?.getJSON();
      return {
        text: docToText(doc),
        empty: isDocEmpty(doc),
        mentionedIds: readMentionIds(doc).join(','),
      };
    },
  }) ?? { text: '', empty: true, mentionedIds: '' };

  useEffect(() => {
    mentionedIdsRef.current = new Set(mentionedIds === '' ? [] : mentionedIds.split(','));
  }, [mentionedIds]);

  useEffect(() => {
    placeholderRef.current = resolvedPlaceholder;
    // Placeholder text is read during decoration, so an empty transaction repaints it.
    if (editor) editor.view.dispatch(editor.state.tr);
  }, [resolvedPlaceholder, editor]);

  useEffect(() => {
    editor?.setEditable(!editorLocked);
    const dom = editor?.view.dom;
    if (!dom) return;
    // A locked field has to read as locked, to a screen reader and to the eye. The old textarea
    // got both from the `disabled` attribute, which a content-editable element does not have.
    // A running turn leaves the field open, so it stays at full opacity and is not aria-readonly.
    if (editorLocked) {
      dom.setAttribute('aria-readonly', 'true');
      dom.classList.add('opacity-50');
    } else {
      dom.removeAttribute('aria-readonly');
      dom.classList.remove('opacity-50');
    }
  }, [editor, editorLocked]);

  // The skill trigger: "/" as the first character, when the workspace has active skills. This is a
  // pure reading of the draft rather than state kept beside it, so the menu can never disagree with
  // the text. Escape records the draft it was dismissed on, and any further edit opens it again.
  const slashQuery =
    hasInvokableSkills && workspaceSlug && text.startsWith('/') && !text.includes(' ') && slashDismissedFor !== text
      ? text.slice(1)
      : null;

  // One definition of "a menu is on screen", used by both the render and the keydown guard. They
  // disagreed before, and the Enter key fell into the gap between them.
  const panelMenuIsOpen = mentionState !== null && mentionState.items.length > 0;
  const skillMenuIsOpen = slashQuery !== null && !!workspaceSlug;

  /**
   * Whether a menu will consume Enter, which is narrower than a menu being on screen.
   *
   * A menu showing a notice — the panel cap reached, or no skill matched — renders, but `MentionMenu`
   * declines every key but Escape, so nothing would act on Enter and the draft became unsendable by
   * keyboard. `activeOptionId` is set exactly when a row is selectable, because that is the row
   * `aria-activedescendant` points at, and a notice clears it. So the one value answers both.
   *
   * Read by the editor's keydown handler, which only runs on a keystroke — after this commit.
   */
  useEffect(() => {
    menuOpenRef.current = activeOptionId !== undefined;
  }, [activeOptionId]);

  /**
   * Point the composer at the open menu, so a screen reader announces the row the user is on.
   *
   * `aria-activedescendant` is only honoured on the focused element, and focus stays here the whole
   * time — putting it on the listbox announces nothing. Both attributes are written to the editor's
   * own DOM node, because `editorProps.attributes` is read once when the editor is built.
   *
   * The role stays `textbox`. The full combobox pattern would swap it to `combobox` while a menu is
   * open, but a role that changes underneath assistive technology mid-interaction is its own problem,
   * and `aria-activedescendant` and `aria-controls` are both valid on a textbox.
   */
  useEffect(() => {
    const dom = editor?.view.dom;
    if (!dom) return;
    // The same flags the render uses. Testing whether the plugin was ACTIVE pointed `aria-controls`
    // at an element that was never on screen, so a screen reader was told about a popup that did not
    // exist — an invalid IDREF on every "@" typed in the main chat.
    const openMenuId = panelMenuIsOpen ? panelMenuId : skillMenuIsOpen ? skillMenuId : null;
    if (openMenuId) {
      dom.setAttribute('aria-controls', openMenuId);
    } else {
      dom.removeAttribute('aria-controls');
    }
    if (openMenuId && activeOptionId) {
      dom.setAttribute('aria-activedescendant', activeOptionId);
    } else {
      dom.removeAttribute('aria-activedescendant');
    }
  }, [editor, panelMenuIsOpen, skillMenuIsOpen, activeOptionId, panelMenuId, skillMenuId]);

  const handleSubmit = useCallback(() => {
    if (!editor || disabled) return;
    const doc = editor.getJSON();
    const typed = docToText(doc).trim();
    const skillIds = pendingSkills.map((s) => s.id);
    // Loading a skill IS the request: the chip alone can send, and the composer writes the sentence the
    // user would otherwise have to type. Without this the send button stayed disabled, and typing
    // filler to get past it made the agent answer the filler instead of running the skill.
    // Naming panels is different and still cannot send on its own: a panel is named inside a sentence,
    // so a mention-only draft has no request in it. With a chip pending, the chips' sentence carries
    // the request and the mention rides along behind it. A lone "/" is what Escape leaves after the
    // reopened skill menu, so it counts as no words too.
    const sentence = skillIds.length > 0 ? runSkillsSentence(pendingSkills) : '';
    const wordless = isDocEmpty(doc) || typed === '/';
    let content = typed;
    if (wordless && sentence) content = typed && typed !== '/' ? `${sentence} ${typed}` : sentence;
    if (!content || (isDocEmpty(doc) && skillIds.length === 0)) return;

    const panelIds = readMentionIds(doc);
    onSend(content, skillIds.length > 0 ? skillIds : undefined, panelIds.length > 0 ? panelIds : undefined);

    editor.commands.clearContent(true);
    setPendingSkills([]);
  }, [editor, disabled, onSend, pendingSkills]);

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  /**
   * Writes a mention in from outside — the ✨ on a dashboard panel.
   *
   * Two rules the `@` menu already applies, and this path skipped:
   *
   * 1. The cap. Seven clicks used to write seven mentions, and the send then reported five, so the
   *    agent read a list of five beside a sentence naming seven.
   * 2. A panel the draft already names is not written twice.
   *
   * It also opens with a space when the cursor sits against a word. Without one, `why did` becomes
   * `why did@Revenue`, which is no longer a mention: the transcript renders it as flat prose, and a
   * restored draft drops the reference while the words still read as if it were there.
   */
  const writeMention = useCallback(
    (mention: PanelMention) => {
      if (!editor) return;
      const doc = editor.getJSON();
      const already = new Set(readMentionIds(doc));
      if (already.has(mention.id)) return;

      const before = editor.state.doc.textBetween(0, editor.state.selection.from, ' ', ' ');
      const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);

      editor
        .chain()
        .insertContent([
          ...(needsLeadingSpace ? [{ type: 'text', text: ' ' }] : []),
          { type: PANEL_MENTION_NODE, attrs: { id: mention.id, label: mention.label } },
          { type: 'text', text: ' ' },
        ])
        .run();

      // TipTap's focus command defers the DOM focus to requestAnimationFrame. A fast keystroke after
      // several queued mentions can land between keydown and beforeinput, dropping its first letter.
      // ProseMirror's view focus is synchronous and restores the transaction's final selection.
      editor.view.focus();
    },
    [editor],
  );

  // Keyed on the nonce and on `editor`. A mention queued before the editor exists is written the
  // moment it does, so the very first ✨ of a session lands like every later one.
  const lastWrittenMentionNonce = useRef(0);
  useEffect(() => {
    if (!editor || !pendingMention || !pendingMentionNonce) return;
    if (pendingMentionNonce === lastWrittenMentionNonce.current) return;
    lastWrittenMentionNonce.current = pendingMentionNonce;
    writeMention(pendingMention);
  }, [editor, pendingMention, pendingMentionNonce, writeMention]);

  // Restore an aborted prompt (empty-only) or stage an explicit starter (replace). Mentions are
  // rebuilt into nodes before the user can edit them.
  useEffect(() => {
    if (!editor || !prefillValue) return;
    if (!prefillReplace && !isDocEmpty(editor.getJSON())) return;
    // The turn's own panels first, so each mention keeps the id it was sent with.
    const mentions = prefillPanelsRef.current?.length ? prefillPanelsRef.current : (panelOptionsRef.current ?? []);
    // Cmd+Z restores the prior draft only after UndoRedo has closed the typing group
    // (`newGroupDelay` 500 ms). A replace inside that window merges with the last keystrokes.
    editor.commands.setContent(textToDoc(prefillValue, mentions));
    if (prefillReplace) editor.commands.focus('end');
  }, [prefillNonce, prefillValue, prefillReplace, editor]);

  // Return focus after an interactive block resolves, once the field can be edited. A running turn
  // leaves it editable, so focus returns right after the answer rather than when the turn ends.
  const lastAutoFocusedNonce = useRef(0);
  useEffect(() => {
    if (autoFocusNonce && autoFocusNonce !== lastAutoFocusedNonce.current && !editorLocked) {
      lastAutoFocusedNonce.current = autoFocusNonce;
      editor?.commands.focus();
    }
  }, [autoFocusNonce, editorLocked, editor]);

  /**
   * The sentence this component last wrote into the draft, or null when it has written none.
   *
   * Kept in a ref rather than state because it is a claim about the document, not something rendered:
   * the draft is the source of truth, and this only records which draft the composer may rewrite.
   */
  const autofilledSentence = useRef<string | null>(null);

  // Picking a skill writes the sentence that skill implies, so the user sees and can edit what will be
  // sent instead of sending a message the composer invented at submit time. The sentence tracks the
  // chips while the draft is still exactly what was written. The first edit hands the draft back to the
  // user, and nothing rewrites it after that.
  useEffect(() => {
    if (!editor) return;
    const current = docToText(editor.getJSON()).trim();
    // Two drafts are the composer's to write: an empty one, and one still holding exactly the
    // sentence it wrote. Anything else holds the user's words and is left alone.
    if (current !== '' && current !== autofilledSentence.current) return;

    const next = pendingSkills.length > 0 ? runSkillsSentence(pendingSkills) : '';
    autofilledSentence.current = next === '' ? null : next;
    if (next === current) return;

    // Written with a trailing space so adding detail is just typing, not typing a space first.
    if (next === '') editor.commands.clearContent(true);
    else editor.commands.setContent(textToDoc(`${next} `, []));
    editor.commands.focus('end');
  }, [editor, pendingSkills]);

  // The skill menu opens on a "/" that starts the draft, so the sentence above would otherwise lock
  // the user out of picking a second skill. A "/" typed against a sentence the composer still owns
  // means the menu, not a longer sentence: drop the sentence and keep the "/".
  useEffect(() => {
    const owned = autofilledSentence.current;
    if (!editor || owned === null) return;
    const typed = text.trim();
    if (!typed.startsWith(owned) || typed.slice(owned.length).trim() !== '/') return;
    autofilledSentence.current = null;
    setSlashDismissedFor(null);
    editor.commands.setContent(textToDoc('/', []));
    editor.commands.focus('end');
  }, [editor, text]);

  const handleSkillSelect = useCallback(
    (skill: { id: string; title: string }) => {
      setPendingSkills((prev) => {
        if (prev.some((s) => s.id === skill.id)) return prev;
        // Cap includes session-loaded + pending — never queue a 6th skill for send.
        if (loadedSkills.length + prev.length >= MAX_SKILLS_PER_CHAT) return prev;
        return [...prev, skill];
      });
      // Clearing the whole draft is safe because `slashQuery` only opens the menu while the draft is
      // "/" plus one space-free token, so there is never anything else in it to lose. A draft that
      // already holds words closes the menu, which is the first of two reasons the sentence below
      // can never overwrite them; the second is the ownership check in the sync effect.
      if (text.startsWith('/')) editor?.commands.clearContent(true);
      // Still needed for the draft the effect declines to touch, where nothing else returns focus.
      setTimeout(() => editor?.commands.focus(), 0);
    },
    [loadedSkills.length, editor, text],
  );

  // Escape is the only way a menu closes without a pick, so this runs on a dismissal alone.
  const handleSkillMenuClose = useCallback(() => {
    setSlashDismissedFor(text);
    // The reopen traded the sentence for the "/" that summoned the menu. No second skill was picked,
    // so the draft goes back to the sentence the chips still imply.
    if (!editor || text.trim() !== '/' || pendingSkills.length === 0) return;
    const restored = runSkillsSentence(pendingSkills);
    autofilledSentence.current = restored;
    editor.commands.setContent(textToDoc(`${restored} `, []));
    editor.commands.focus('end');
  }, [text, editor, pendingSkills]);

  const handleMentionSelect = useCallback(
    (mention: PanelMention) => {
      mentionState?.choose(mention);
    },
    [mentionState],
  );

  const handleMentionClose = useCallback(() => setMentionState(null), []);

  const removePendingSkill = useCallback(
    (id: string) => {
      // An edited draft is the user's, so the sync effect below leaves it alone — including the
      // sentence this composer wrote in front of the words. That sentence names a skill the send no
      // longer carries, so it goes with the chip while the user's words stay.
      const removed = pendingSkills.find((s) => s.id === id);
      const current = editor ? docToText(editor.getJSON()) : '';
      const stale = [runSkillsSentence(pendingSkills), ...(removed ? [runSkillsSentence([removed])] : [])].find(
        (prefix) => current.startsWith(prefix),
      );
      if (editor && stale && current.trim() !== autofilledSentence.current) {
        // Deleted as a range, not rewritten from text, so mentions later in the draft keep their ids.
        const cut = stale.length + (/^ */.exec(current.slice(stale.length))?.[0].length ?? 0);
        autofilledSentence.current = null;
        editor.commands.deleteRange({ from: 1, to: 1 + cut });
      }
      setPendingSkills((prev) => prev.filter((s) => s.id !== id));
      // Deferred past the click that removed the chip, which otherwise takes focus back.
      // The sync effect focuses the composer when it rewrites or clears the draft, so this is here
      // for the case it returns early from: a draft the user has edited and now owns.
      setTimeout(() => editor?.commands.focus('end'), 0);
    },
    [editor, pendingSkills],
  );

  // A pending skill counts as something to send, so the button is live on a chip alone.
  const hasContent = !empty || pendingSkills.length > 0;

  // Chip strip: already-loaded skills (non-removable) + pending skills (removable). Panels are not
  // here on purpose — a panel is named inside the sentence, while a skill is a mode set on the whole
  // message, so only the skill has nothing to say in the middle of a line.
  const showChips = loadedSkills.length > 0 || pendingSkills.length > 0;
  const occupiedSkillIds = useMemo(
    () => new Set([...loadedSkills.map((s) => s.id), ...pendingSkills.map((s) => s.id)]),
    [loadedSkills, pendingSkills],
  );

  return (
    // Width-agnostic: always full-width. The parent owns max-width — ChatWelcome
    // caps the hero at 680px, ChatConversation caps chat at max-w-4xl.
    <div className={cn('w-full', !isHero && !embedded && 'px-4 pt-2 pb-4')}>
      {stopStalled && (
        <Callout variant="warning">
          Still stopping. The server running this reply may be restarting. You can start a new chat.
        </Callout>
      )}
      {stopStatusError && !stopStalled && (
        <Callout variant="error">
          {stopping
            ? 'Could not confirm whether the turn stopped. Send remains unavailable.'
            : 'Could not confirm whether this turn finished. Send remains unavailable.'}
          {onRetryStopStatus && (
            <Button variant="secondary" size="sm" onClick={onRetryStopStatus} label="Retry status" />
          )}
        </Callout>
      )}
      {/* Floating container */}
      {/* The textarea had a full-box `<label htmlFor>` overlay, so a click on the card's padding
          focused it. A content-editable element cannot be a label target, so the card forwards the
          click itself — without this, clicking the bottom strip does nothing. Clicks that land on a
          real control are left alone. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- forwards a click to the editor inside it; the interactive element is that child, and it stays fully keyboard-reachable */}
      <div
        ref={containerRef}
        onMouseDown={(e) => {
          if (editorLocked) return;
          if ((e.target as HTMLElement).closest('button, [role="listbox"], [contenteditable]')) return;
          e.preventDefault();
          editor?.commands.focus('end');
        }}
        className={cn(
          'group relative cursor-text border border-input bg-card transition-all duration-200',
          'focus-within:border-brand focus-within:shadow-primary',
          isHero ? 'rounded-2xl' : 'rounded-xl',
        )}
      >
        {/* SkillMenu anchored above the container */}
        {skillMenuIsOpen && slashQuery !== null && (
          <SkillMenu
            query={slashQuery}
            alreadyLoadedIds={occupiedSkillIds}
            onSelect={handleSkillSelect}
            onClose={handleSkillMenuClose}
            id={skillMenuId}
            onActiveOptionChange={setActiveOptionId}
          />
        )}

        {/* PanelMentionMenu anchored above the container. A query that matches nothing closes the
            menu rather than showing an empty box over the sentence — and closing it is also what
            hands the Enter key back to the composer. */}
        {panelMenuIsOpen && mentionState !== null && (
          <PanelMentionMenu
            mentions={mentionState.items}
            onSelect={handleMentionSelect}
            onClose={handleMentionClose}
            id={panelMenuId}
            onActiveOptionChange={setActiveOptionId}
          />
        )}

        {/* Chip strip: loaded + pending skills */}
        {showChips && (
          <div className={cn('flex flex-wrap gap-1 pt-2', isHero ? 'px-6' : 'px-3')} data-testid="skill-chip-strip">
            {/* Already loaded: non-removable, dimmed */}
            {loadedSkills.map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground"
                data-testid={`loaded-skill-chip-${skill.id}`}
              >
                {skill.title}
              </span>
            ))}
            {/* Pending: removable, pre-send */}
            {pendingSkills.map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-xs text-brand"
                data-testid={`pending-skill-chip-${skill.id}`}
              >
                {skill.title}
                <button
                  type="button"
                  onClick={() => removePendingSkill(skill.id)}
                  aria-label={`Remove skill ${skill.title}`}
                  className="relative ml-0.5 rounded-full hover:bg-brand/20"
                  data-testid={`remove-skill-chip-${skill.id}`}
                >
                  <X className="size-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* The editor */}
        <EditorContent editor={editor} />

        {/* Bottom bar: send button right */}
        <div className={cn('ml-auto flex w-fit items-end', isHero ? 'px-4 pb-4' : 'px-2 pb-2')}>
          {/* Right: stop (disabled + stoppable) / spinner (disabled) / send */}
          {disabled && stopping ? (
            <Button disabled loading label="Stopping…" aria-label="Stopping" size="md" />
          ) : disabled && agentLoaded && onStop ? (
            <button
              onClick={onStop}
              className={cn(
                'relative flex items-center justify-center transition-all duration-150',
                isHero ? 'h-9 w-9 rounded-xl' : 'h-7 w-7 rounded-md',
                // Stop button uses bg-foreground (not bg-destructive) — it's a neutral interrupt, not destructive.
                'bg-foreground text-background hover:bg-foreground/85',
              )}
              aria-label="Stop generating"
            >
              <Square size={isHero ? 14 : 12} strokeWidth={0} fill="currentColor" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={disabled || !hasContent}
              className={cn(
                'relative flex items-center justify-center transition-all duration-150',
                isHero ? 'h-9 w-9 rounded-xl' : 'h-7 w-7 rounded-md',
                hasContent && !disabled
                  ? 'bg-brand text-primary-foreground shadow-[0_18px_36px_-24px_rgba(16,94,213,0.85)] hover:bg-brand/90'
                  : 'bg-muted-foreground/10 text-muted-foreground/30',
                'disabled:cursor-not-allowed',
              )}
              aria-label={disabled ? 'Processing...' : 'Send message'}
            >
              {disabled ? (
                <LoaderCircle size={isHero ? 16 : 14} strokeWidth={2.5} className="animate-spin" />
              ) : (
                <ArrowUp size={isHero ? 16 : 14} strokeWidth={2.5} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Hint text below input */}
      <p
        className={cn('text-center text-muted-foreground/50', isHero ? 'mt-2 text-xs' : 'mt-1.5 text-[0.625rem]')}
        data-testid="ai-disclosure"
      >
        {AI_DISCLOSURE_TEXT}
      </p>
      {!isHero && !isSidebar && (
        <p className="mt-1.5 text-center text-[0.625rem] text-muted-foreground/50">
          Enter to send · Shift+Enter for new line
          {hasInvokableSkills && ' · / to invoke a skill'}
          {canMentionPanels && ' · @ to mention a panel'}
        </p>
      )}
      {isSidebar && canMentionPanels && (
        <p className="mt-1.5 text-center text-[0.625rem] text-muted-foreground/50">
          Type <span className="font-mono text-foreground/70">{MENTION_TRIGGER}</span> to mention a panel
        </p>
      )}
      {isHero && hasInvokableSkills && (
        <p className="mt-2 text-center text-xs text-muted-foreground/60">
          Type <span className="font-mono text-foreground/70">/</span> to invoke a skill
        </p>
      )}
    </div>
  );
}
